import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { date, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import { sumBy, xorBy } from 'lodash';
import claimSetStatusPayedApi from 'models/claims/claim/setStatusPayed/Api.Claim.SetStatusPayed';
import changeClaimJobItemStatus from 'models/claims/claimJobItem/changeStatus/Api.ClaimJobItem.ChangeStatus';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import PaymentStatus from 'models/enums/PaymentStatus';
import PaymentType from 'models/enums/PaymentType';
import claimJobPaymentSaveApi from 'models/finance/claimJobPayment/save/Api.ClaimJobPayment.Save';
import claimJobPaymentItemExportApi from 'models/finance/claimJobPaymentItem/export/Api.ClaimJobPaymentItem.Export';
import claimJobPaymentItemListApi from 'models/finance/claimJobPaymentItem/list/Api.ClaimJobPaymentItem.List';
import ClaimJobPaymentItem from 'models/finance/claimJobPaymentItem/Model.ClaimJobPaymentItem';
import PostingAccount from 'models/finance/postingAccount/Model.PostingAccount';
import { useMemo, useState } from 'react';
import { financeClaimJobPaymentItemIndexRoute } from './Finance.ClaimJobPaymentItem.Index';

export enum ClaimJobPaymentItemAction {
  ADD = 'add',
  CREATE_PAYMENT = 'createPayment',
  INFO = 'info',
  PROCESS_PAYMENT = 'processPayment',
}

export enum FinanceClaimJobPaymentItemInfoTab {
  INFO = 'info',
  INVOICES = 'invoices',
}

export type VerifyClaimAction = Utils.VerifyExtends<
  Module.Finance.ClaimJobPaymentItem.Actions,
  Utils.ValueOf<typeof ClaimJobPaymentItemAction>
>;

export type PaymentRequestEmailData = {
  claimJobPaymentItem: Model.IClaimJobPaymentItem;
  emailData?: Utils.Email.Data;
};

export function useClaimJobPaymentItemRouter() {
  return usePageRouter<Module.Finance.ClaimJobPaymentItem.Params, Module.Finance.ClaimJobPaymentItem.Query>({
    route: financeClaimJobPaymentItemIndexRoute,
  });
}
export default function useClaimJobPaymentItem() {
  const { lang } = getLocales();
  const pageRouter = useClaimJobPaymentItemRouter();

  const { permissions, user } = useAuthStore();

  const [selectedItems, setSelectedItems] = useState<ClaimJobPaymentItem[]>([]);
  const [paymentRequestEmailData, setPaymentRequestEmailData] = useState<PaymentRequestEmailData>();

  const canView = permissions.VIEW_CLAIMJOBPAYMENTITEM;

  //#region Api calls
  const listApi = useApi(
    {
      action: ClaimJobPaymentItem.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        hasClaimJobPayment: false,
        isCancelled: false,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !canView,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const findApi = useApi(
    {
      action: ClaimJobPaymentItem.find,
      wait: !canView || !pageRouter.params.id,
      body: {
        id: Number(pageRouter.params.id),
      },
    },
    [pageRouter.params.id]
  );

  function reload() {
    listApi.execute((b) => b);
    findApi.execute((b) => b);
  }
  //#endregion

  const userApi = useApi({ action: User.list, body: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    body: {
      id: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: true,
  });
  const postingAccountListApi = useApi(
    {
      action: PostingAccount.list,
      default: {
        isActive: true,
        isPayable: true,
        currency: selectedItems[0]?.currency,
        productId: selectedItems[0]?.claimJob?.claim?.contract?.productId
          ? [selectedItems[0]?.claimJob?.claim?.contract?.productId]
          : undefined,
      },
      wait: !selectedItems[0]?.claimJob?.claim?.contract?.productId || !selectedItems[0]?.currency,
    },
    [selectedItems[0]?.claimJob?.claim?.contract?.productId, selectedItems[0]?.currency]
  );

  const [paymentCount, setPaymentCount] = useState<number>(listApi.payload?.data?.length);
  async function getAllSelectedClaimJobPaymentItems(): Promise<Model.IClaimJobPaymentItem[]> {
    const newQuery = { ...pageRouter?.query?.list };
    delete newQuery.limit;
    delete newQuery.offset;

    const {
      payload: { data: filteredItems },
    } = await claimJobPaymentItemListApi({ ...newQuery });

    return selectedItems?.length === filteredItems?.length ? [] : (filteredItems as Model.IClaimJobPaymentItem[]);
  }

  const toggleItem = (claimJobPaymentItem: ClaimJobPaymentItem) => {
    const newSelectedItemList = xorBy(selectedItems || [], [claimJobPaymentItem], 'id');
    setSelectedItems(newSelectedItemList);
  };

  const toggleSelectAll = async (selectAll: boolean) => {
    if (selectAll) {
      const fetchedItems = await getAllSelectedClaimJobPaymentItems();
      setPaymentCount(fetchedItems.length);
      setSelectedItems(fetchedItems.map((it) => new ClaimJobPaymentItem(it)));
    } else {
      setSelectedItems([]);
      setPaymentCount(listApi.payload?.data?.length);
    }
  };

  const payment = useMemo(() => {
    const payee = selectedItems[0]?.claimJobItemPayee;
    const claimData = selectedItems[0]?.claimJob?.claim;
    const claimJobItems = selectedItems.flatMap((claimJobPaymentItem) =>
      claimJobPaymentItem.claimJob.claimJobItems.filter((it) => it.claimJobItemPayee.typeAndId === payee.typeAndId)
    );
    const paymentMethodCode = selectedItems[0]?.paymentMethodCode;
    const gstHstAmountPaid = sumBy(selectedItems || [], (item) => Number(item.gstHstAmount) || 0);
    const provincialSalesTaxAmountPaid = sumBy(
      selectedItems || [],
      (item) => Number(item.provincialSalesTaxAmount) || 0
    );
    const totalSum = sumBy(selectedItems, (item) => Number(item.total) ?? 0);
    const deduction = sumBy(selectedItems, (item) => Number(item.totalDeductibleAmount) ?? 0);

    return {
      claim: claimData,
      claimJobItems,
      payee,
      payeeEmail: payee?.getEmailForPayment({ clientOverrideEmail: claimData?.contactEmail }),
      paymentMethodCode,
      gstHstAmountPaid,
      provincialSalesTaxAmountPaid,
      totalSum,
      deduction,
      currentDate: dayjs().format(defaultServerDateTimeFormat),
      currency: selectedItems[0]?.currency,
      isETransferOrUsBank: paymentMethodCode === PaymentType.usBank || paymentMethodCode === PaymentType.ETransfer,
      isCancelled: selectedItems.some((it) => it.isCancelled || it.claimJobPayment?.isCancelled),
    };
  }, [selectedItems]);

  const createClaimJobPayment = async (data: Api.Finance.ClaimJobPayment.Save.IRequest) => {
    if (!selectedItems) {
      addLog({ error: lang.mustSelectAtleastOneOption });
      return;
    }
    if (selectedItems.some((it) => it.isCancelled || it.claimJobPayment?.isCancelled)) {
      addLog({ error: 'Selected payment items have been cancelled!' });
      return;
    }

    const claimResponse = await claimJobPaymentSaveApi({
      ...data,
      currency: selectedItems[0]?.currency,
      claimJobPaymentItems: selectedItems.map((it) => ({ id: it.id })),
      claimJobPaymentPayee: {
        payeeId: payment?.payee?.payeeId,
        clientId: payment?.payee?.clientId,
        dealerId: payment?.payee?.dealerId,
        payeeTypeId: payment?.payee?.payeeTypeId,
      } as ClaimJobItemPayee,
    });

    if (claimResponse.status === HttpStatus.ok) {
      const responses = await Promise.all(
        payment.claimJobItems.map((claimJobItem) =>
          changeClaimJobItemStatus(
            claimJobItem.id,
            claimJobItem.adjudication > 0 ? PaymentStatus.paid : PaymentStatus.denied
          )
        )
      );

      if (responses.every((r) => r?.status === HttpStatus.ok)) {
        await claimSetStatusPayedApi({ id: payment?.claim?.id });
      }

      setSelectedItems([]);
      listApi.execute((b) => b);
      addLog({ success: claimResponse.message || lang.saveSuccess });
    } else {
      addLog({ error: claimResponse.message || lang.error });
    }
  };

  async function exportToExcel() {
    const query = { ...pageRouter.query.list };
    delete query.limit;
    delete query.offset;

    const res = await claimJobPaymentItemExportApi({ ...query });

    if (res?.status === HttpStatus.ok) {
      autoTriggerDownload({
        url: generateDataUrl(MimeType.appOPEN_XML_SHEET, res?.payload),
        name: `${lang.paymentsPending}_${date().format('DD-MM-YY')}.xlsx`,
      });
      addLog({ success: lang.exportSuccess });
    } else addLog({ error: lang.exportError });
  }

  return {
    listApi,
    userApi,
    dealersApi,
    pageRouter,
    permissions,
    postingAccountListApi,
    selected: pageRouter.params.id ? findApi?.payload : null,
    canView,
    toggleItem,
    toggleSelectAll,
    selectedItems,
    createClaimJobPayment,
    reload,
    paymentRequestEmailData,
    setPaymentRequestEmailData,
    payment,
    exportToExcel,
    getAllSelectedClaimJobPaymentItems,
    paymentCount,
    views: {
      [ClaimJobPaymentItemAction.CREATE_PAYMENT]: pageRouter.params.action === ClaimJobPaymentItemAction.CREATE_PAYMENT,
    },
  };
}

export function useClaimJobPaymentItemProvider() {
  return useDataProvider<ReturnType<typeof useClaimJobPaymentItem>>();
}
